/** @format */

import { AnalyticsService, BreakpointsService } from '@shared/data-access/services';
import {
	Component,
	ComponentRef,
	TemplateRef,
	computed,
	effect,
	inject,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import {
	HmfButtonComponent,
	HmfDividerComponent,
	HmfDrawerComponent,
	HmfModalFactoryService
} from '@dsg-tech/homefield-angular';
import {
	getAllWarranties,
	selectAemContentIdentifier,
	selectProductPartNumber,
	selectSelectedWarrantyPlanSku,
	selectWarrantiesForResolvedSku,
	selectWarranty,
	selectWarrantyCallState
} from '@pdp/data-access/state';

import { BreakPointNew } from '@shared/data-access/enums';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { WarrantyInfoComponent } from '@dsg-tech/non-merch-common-externals';
import { WarrantyPlanSelectorComponent } from '../warranty-plan-selector/warranty-plan-selector.component';

@Component({
	selector: 'pdp-component-warranty-selection-drawer',
	imports: [
		CommonModule,
		WarrantyPlanSelectorComponent,
		HmfButtonComponent,
		WarrantyInfoComponent,
		HmfDividerComponent
	],
	templateUrl: './warranty-selection-drawer.component.html',
	styleUrl: './warranty-selection-drawer.component.scss'
})
export class WarrantySelectionDrawerComponent {
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly store = inject(Store);
	private readonly analyticsService = inject(AnalyticsService);

	private drawerRef: ComponentRef<HmfDrawerComponent>;
	readonly drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');
	readonly drawerDirection = computed(() =>
		this.breakpointsService.breakpoint() <= BreakPointNew.SMALL ? 'bottom' : 'right'
	);

	readonly previouslySelectedPlanSku = this.store.selectSignal(selectSelectedWarrantyPlanSku);
	readonly selectedPlanSku = signal(undefined);
	readonly availableWarranties = this.store.selectSignal(selectWarrantiesForResolvedSku);
	readonly warrantyCallState = this.store.selectSignal(selectWarrantyCallState);
	readonly productEcode = this.store.selectSignal(selectProductPartNumber);
	readonly aemContentIdentifier = this.store.selectSignal(selectAemContentIdentifier);
	readonly panelOpened = signal(false);
	readonly shouldShowWarrantyInfo = computed(
		() =>
			this.warrantyCallState() !== 'error' &&
			!(this.warrantyCallState() === 'success' && this.availableWarranties().length === 0)
	);

	constructor() {
		// When we update to Angular v20, we should update the modal service to make use of input binding instead
		// so we can just do that when we create the drawer instead of having to manually setInput in an effect
		// https://github.com/angular/angular/pull/60137
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.drawerRef) {
				this.drawerRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
				this.drawerRef.setInput('direction', this.drawerDirection());
			}
		});

		effect(() => {
			if (this.warrantyCallState() === 'success' && untracked(this.panelOpened)) {
				if (untracked(this.availableWarranties)?.length === 0) {
					this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
						event: 'warranty',
						value: 'pdpNoPlan'
					});
					return;
				}

				this.analyticsService.triggerAnalyticsEvent('WarrantyView', {
					ProductID: untracked(this.productEcode),
					WarrantyEcode: untracked(this.availableWarranties)?.[0]?.planEcode,
					WarrantyFindingMethod: 'Product Details'
				});
			}
		});
	}

	open() {
		if (this.warrantyCallState() !== 'success') {
			this.store.dispatch(getAllWarranties());
		}

		this.drawerRef = this.modalFactoryService.create(
			HmfDrawerComponent,
			{
				closable: true,
				direction: this.drawerDirection(),
				size: this.pdslUtilityService.getValueByScreenSize(
					DynamicDrawerSizes,
					this.breakpointsService.breakpoint()
				)
			},
			this.drawerContent()
		);
		this.selectedPlanSku.set(this.previouslySelectedPlanSku() ?? null);
		this.drawerRef.instance.openDrawer();
		this.drawerRef.instance.closed.subscribe(() => {
			this.drawerRef.destroy();
		});

		this.panelOpened.set(true);
	}

	handleSaveSelectionClick() {
		this.store.dispatch(selectWarranty({ selectedPlanSku: this.selectedPlanSku() }));
		this.drawerRef.instance.closeDrawer();
	}
}
